/**
 * Secure, read-only comparison while an admin records formal checks.
 * No source is downloaded to a device just to read it; no public object URLs
 * or third-party document embeds. Two panes share the original check form.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, Loader2, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';
import API from '../../utils/apiBase';
import { fetchProtectedFileBlob, revokeBlobUrl } from '../../lib/protectedFiles';
import { collectVerificationWorkbenchFiles, safeWorkbenchFileUrl, CHECK_GROUPS } from '../../lib/verificationWorkbenchFiles';

pdfjs.GlobalWorkerOptions.workerSrc = '//unpkg.com/pdfjs-dist@' + pdfjs.version + '/build/pdf.worker.min.mjs';

function sniffPreviewType(buffer) {
  const b = new Uint8Array(buffer);
  if (b.length >= 4 && b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46) return 'pdf';
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image';
  if (b.length >= 4 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return 'image';
  if (b.length >= 12 && String.fromCharCode(...b.slice(0, 4)) === 'RIFF'
      && String.fromCharCode(...b.slice(8, 12)) === 'WEBP') return 'image';
  return 'unsupported';
}

function SecureEvidencePane({ document, token, position }) {
  const [blobUrl, setBlobUrl] = useState('');
  const [type, setType] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    let ownedUrl = '';
    const controller = new AbortController();
    setBlobUrl('');
    setPage(1);
    setPages(1);
    setZoom(1);
    setRotation(0);
    setError('');
    setType('');
    if (!document || !token) return () => {
      live = false;
      controller.abort();
    };
    const path = safeWorkbenchFileUrl(document.id, API);
    if (!path) {
      setError('This evidence reference is not valid.');
      return () => {
        live = false;
        controller.abort();
      };
    }
    setLoading(true);
    (async () => {
      try {
        // Existing authenticated document route enforces staff + tenant access.
        const file = await fetchProtectedFileBlob(path, token, { signal: controller.signal });
        ownedUrl = file.blobUrl;
        const kind = sniffPreviewType(await file.blob.slice(0, 12).arrayBuffer());
        if (kind === 'unsupported') throw new Error('Unsupported or damaged file. Use the original secure document viewer.');
        if (live) {
          setType(kind);
          setBlobUrl(ownedUrl);
          setError('');
        }
      } catch (err) {
        revokeBlobUrl(ownedUrl);
        ownedUrl = '';
        if (live && !controller.signal.aborted) setError(err?.response?.status === 403 || err?.response?.status === 404
          ? 'This file is not accessible to your account. No verification was changed.'
          : 'The document could not be previewed. Use the existing secure evidence viewer.');
      } finally {
        if (!live) revokeBlobUrl(ownedUrl);
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
      controller.abort();
      revokeBlobUrl(ownedUrl);
    };
  }, [document?.id, token]);

  return (
    <div className="min-w-0 min-h-0 flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={'verification-evidence-pane-' + position}>
      <div className="flex flex-wrap items-center justify-between gap-1 border-b bg-slate-50 px-2 py-1.5">
        <p className="max-w-[85%] truncate text-xs font-semibold text-slate-800" title={document?.name}>
          {document?.name || 'Select a document'}
        </p>
        {blobUrl && (
          <div className="flex items-center gap-1">
            <button type="button" aria-label="Zoom out" onClick={() => setZoom(z => Math.max(0.7, z - 0.2))}><ZoomOut size={15} /></button>
            <button type="button" aria-label="Zoom in" onClick={() => setZoom(z => Math.min(2.5, z + 0.2))}><ZoomIn size={15} /></button>
            <button type="button" aria-label="Rotate view" onClick={() => setRotation(r => (r + 90) % 360)}><RotateCw size={15} /></button>
          </div>
        )}
      </div>
      <div className="min-h-[16rem] flex-1 overflow-auto bg-slate-100 p-2 text-center lg:min-h-0">
        {!document && <p className="p-4 text-xs text-slate-600">Choose a document above.</p>}
        {loading && <p className="flex items-center justify-center gap-2 p-4 text-xs text-blue-800"><Loader2 className="animate-spin" size={16} /> Loading private file…</p>}
        {error && <p className="p-4 text-xs font-medium text-amber-800" role="alert">{error}</p>}
        {!loading && blobUrl && type === 'image' && (
          <img src={blobUrl} alt={'Private evidence: ' + (document?.name || 'document')}
            className="mx-auto h-auto max-w-full rounded object-contain"
            style={{ transform: 'rotate(' + rotation + 'deg) scale(' + zoom + ')', transformOrigin: 'center center' }} />
        )}
        {!loading && blobUrl && type === 'pdf' && (
          <Document file={blobUrl} onLoadSuccess={({ numPages }) => setPages(numPages)}
            onLoadError={() => setError('This PDF could not be displayed. Use the original secure document viewer.')}
            loading={<p className="text-xs">Rendering PDF…</p>}>
            <Page pageNumber={page} width={365} scale={zoom} rotate={rotation}
              renderAnnotationLayer={false} renderTextLayer={false} />
          </Document>
        )}
      </div>
      {blobUrl && type === 'pdf' && pages > 1 && (
        <div className="flex items-center justify-center gap-3 border-t p-1 text-xs">
          <button type="button" aria-label="Previous page" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={16} /></button>
          Page {page} / {pages}
          <button type="button" aria-label="Next page" disabled={page >= pages} onClick={() => setPage(p => p + 1)}><ChevronRight size={16} /></button>
        </div>
      )}
    </div>
  );
}

export default function VerificationEvidenceWorkbench({ open, employeeId, checkType, token }) {
  const currentGroup = checkType === 'dbs_status_check' ? 'dbs'
    : checkType === 'right_to_work_check' ? 'right_to_work'
      : checkType === 'address_verification' ? 'proof_of_address'
        : 'identity';
  const [files, setFiles] = useState([]);
  const [leftId, setLeftId] = useState('');
  const [rightId, setRightId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setFiles([]);
    setLeftId('');
    setRightId('');
    setError('');
    if (!open || !employeeId || !token) return () => { active = false; };
    setLoading(true);
    axios.get(API + '/employees/' + encodeURIComponent(employeeId) + '/compliance-file', {
      headers: { Authorization: 'Bearer ' + token },
      timeout: 25000,
    }).then(({ data }) => {
      if (!active) return;
      if (data?.status_unavailable || data?.stale_data_preserved ||
          data?.serializer_version !== 'dual_row_v1') {
        throw new Error('Current verification evidence is temporarily unavailable.');
      }
      const docs = collectVerificationWorkbenchFiles(data, currentGroup).slice(0, 36);
      setFiles(docs);
      setLeftId(docs[0]?.id || '');
      setRightId(docs[1]?.id || '');
    }).catch(() => {
      if (active) setError('Current documents could not be loaded. No check has been changed.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, employeeId, token, currentGroup]);

  const left = files.find(f => f.id === leftId);
  const right = files.find(f => f.id === rightId);
  const optionLabel = file => {
    const title = CHECK_GROUPS.find(([key]) => key === file.group)?.[1] || 'Evidence';
    return title + (file.isProof ? ' — Check proof' : '') + ' — ' + file.name;
  };
  return (
    <section className="flex min-h-0 flex-col gap-3 rounded-xl border border-blue-200 bg-blue-50/30 p-3" data-testid="verification-evidence-workbench" aria-label="Private check evidence comparison">
      <div>
        <h3 className="text-sm font-bold text-blue-950">View evidence while recording the check</h3>
        <p className="mt-1 text-xs text-slate-600">
          Compare two staff documents side by side. Original files stay in CareDock's protected storage.
          Viewing a file does not complete the employer verification.
        </p>
      </div>
      {loading && <p className="text-xs text-blue-800">Loading staff evidence register…</p>}
      {error && <p className="text-xs text-amber-800" role="alert">{error}</p>}
      {!loading && !error && files.length === 0 && (
        <p className="text-xs text-amber-800">No accessible evidence was returned for this check. Use the existing secure evidence list or refresh the record.</p>
      )}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 xl:grid-cols-2">
        {[
          { id: leftId, set: setLeftId, file: left, label: 'Document A' },
          { id: rightId, set: setRightId, file: right, label: 'Document B (optional)' },
        ].map((panel, index) => (
          <div className="flex min-h-0 min-w-0 flex-col gap-2" key={panel.label}>
            <label className="text-xs font-semibold text-slate-800">
              {panel.label}
              <select className="mt-1 w-full rounded-lg border border-slate-300 bg-white p-2 text-xs"
                data-testid={'verification-document-select-' + index}
                value={panel.id} onChange={e => panel.set(e.target.value)}>
                <option value="">Select a private document</option>
                {files.map(file => <option key={file.id} value={file.id}>{optionLabel(file)}</option>)}
              </select>
            </label>
            <div className="min-h-[18rem] flex-1">
              <SecureEvidencePane document={panel.file} token={token} position={index} />
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-600">
        Check details against the actual documents and official sources. Do not copy DOB,
        share codes or DBS numbers into notes unless explicitly required for a protected employer check.
      </p>
    </section>
  );
}
